import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { angularPlugin } from '@datadog/browser-rum-angular';

import { version as appVersion } from '../../../package.json';
import { redactSignedUrls } from './redact-signed-urls';

/**
 * Datadog environment tag. Mirrors the deployment targets:
 * production -> 'hypecast.one', QA -> 'hypecast.dev'.
 */
export type DatadogEnv = 'hypecast.one' | 'hypecast.dev';

const DATADOG_SITE = 'datadoghq.eu';
const SERVICE_NAME = 'admin-console';

/**
 * Initializes Datadog Logs and RUM for the given environment.
 * Must be called before the Angular app bootstraps so the RUM Angular
 * plugin is registered before the router starts navigating.
 */
export function initDatadogMonitoring(env: DatadogEnv): void {
  datadogLogs.init({
    clientToken: 'puba04ca3df02f8a665c25814136dbf4d42',
    site: DATADOG_SITE,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: SERVICE_NAME,
    env,
    beforeSend: (event) => {
      // A failed download is reported with the serialized HttpErrorResponse in
      // the message, and that carries the pre-signed url verbatim.
      event.message = redactSignedUrls(event.message);
      if (event.error) {
        event.error.message = redactSignedUrls(event.error.message);
        event.error.stack = redactSignedUrls(event.error.stack);
      }
      if (event.http) {
        event.http.url = redactSignedUrls(event.http.url);
      }
      return true;
    },
  });

  datadogRum.init({
    applicationId: 'a107e31c-8610-411d-be9c-b1082d248fd2',
    clientToken: 'pubbb01adb9e0c8bbec7e4ea4c74c60ca02',
    site: DATADOG_SITE,
    service: SERVICE_NAME,
    env,
    version: appVersion,
    sessionSampleRate: 100,
    // Replay is sampled in for every session, but recording does not start on
    // its own: SessionReplayService switches it on only inside the work-heavy
    // sections (video editor, audio editor, analytics, podcasts). Sampling is
    // decided once per session, so a per-route rate is not expressible here --
    // 100 % + manual start is what gives those sections full coverage while a
    // session that never leaves login/settings produces no replay at all.
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [angularPlugin({ router: true })],
    // `trackResources` records the full request url. Files are fetched through
    // pre-signed storage urls whose query string IS the credential, so it never
    // reaches the backend.
    beforeSend: (event) => {
      if (event.type === 'resource') {
        event.resource.url = redactSignedUrls(event.resource.url);
      }
      if (event.type === 'error') {
        event.error.message = redactSignedUrls(event.error.message);
        event.error.stack = redactSignedUrls(event.error.stack);
        if (event.error.resource) {
          event.error.resource.url = redactSignedUrls(event.error.resource.url);
        }
      }
      return true;
    },
  });
}
